import React, { useState, useCallback } from 'react'
import { connect } from 'react-redux'
import { push } from 'react-router-redux'
import { List, Typography, Spin, Empty, Button, Dropdown, Tag } from 'antd'
import {
  MoreOutlined,
  CheckOutlined,
  MessageOutlined,
  FileTextOutlined,
  CalendarOutlined,
  BellOutlined
} from '@ant-design/icons'
import moment from 'moment'
import _ from 'lodash'

import formatUtils from '../../utils/format'
import { readNotifications, getUserNotifications } from '../../reducers/notification/resources'
import mixpanel from '../../utils/mixpanel'
import { switchActiveProfile } from '../../utils/profile-switch'
import session from '../../utils/session'

const { Text } = Typography

interface Notification {
  id: string
  contact_id: string
  contact: {
    name: string
    avatar?: string
  }
  botmap: {
    bot: {
      name: string
    }
  }
  message: string
  header: string
  is_read: boolean
  created_at: string
  viaUserId?: string | null
  brand?: { id: string; name: string } | null
  targetUser?: { id: string; name: string } | null
}

interface NotificationListProps {
  count: number
  notificationList: Notification[]
  handleSetNotificationList: (notifications: Notification[], unreadCount: number) => void
  goTo: (url: string) => () => void
}

const NotificationListAntd: React.FC<NotificationListProps> = ({
  count,
  notificationList,
  handleSetNotificationList,
  goTo,
}) => {
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)

  // Multi-brand: badge every notification with its brand so the parent can
  // distinguish own-brand from sibling-brand at a glance. Solo users never
  // see the chip (always their own brand — no signal).
  const userData = session.get('userData') || {}
  const isMultiBrand = !!(userData.allProfiles && userData.allProfiles.length > 1)

  const formatTimeAgo = (dateStr: string) => {
    const now = moment()
    const notifDate = moment(dateStr)
    const diff = now.diff(notifDate)
    const duration = moment.duration(diff)

    if (duration.asSeconds() < 59) {
      return `${parseInt(String(duration.asSeconds()))} sec ago`
    } else if (duration.asMinutes() < 59) {
      return `${parseInt(String(duration.asMinutes()))} min ago`
    } else if (duration.asHours() < 24) {
      return `${parseInt(String(duration.asHours()))} hours ago`
    } else if (duration.asDays() < 7) {
      return `${parseInt(String(duration.asDays()))} days ago`
    } else if (duration.asWeeks() < 4) {
      return `${parseInt(String(duration.asWeeks()))} weeks ago`
    } else {
      return notifDate.format('MMM D')
    }
  }

  const handleClickNotif = async (notification: Notification) => {
    mixpanel.track('notification:click notification', { notification })

    if (!notification.is_read) {
      await readNotifications({ is_read: [notification.id] })

      const updatedNotifications = notificationList.map(n =>
        n.id === notification.id ? { ...n, is_read: true } : n
      )
      const newUnreadCount = updatedNotifications.filter(n => !n.is_read).length
      handleSetNotificationList(updatedNotifications, newUnreadCount)
    }

    // Multi-brand sibling click: swap the persisted session, then hard-reload
    // into the contact route. A soft react-router push leaves Redux state
    // (selectedCompany, mounted bots, etc.) bound to the previous account, so
    // the contact page fails to load. window.location.assign reboots the SPA
    // with the freshly persisted token + companyId.
    if (notification.viaUserId) {
      const swapped = switchActiveProfile(notification.viaUserId)
      if (swapped) {
        window.location.assign(`/contacts/${notification.contact_id}`)
        return
      }
      console.warn('[notif] could not switch to profile', notification.viaUserId)
    }

    goTo(`/contacts/${notification.contact_id}`)()
  }

  const handleMarkAsRead = async (e: React.MouseEvent, notification: Notification) => {
    e.stopPropagation()
    if (!notification.is_read) {
      await readNotifications({ is_read: [notification.id] })
      const updatedNotifications = notificationList.map(n => 
        n.id === notification.id ? { ...n, is_read: true } : n
      )
      const newUnreadCount = updatedNotifications.filter(n => !n.is_read).length
      handleSetNotificationList(updatedNotifications, newUnreadCount)
    }
  }

  const loadMore = async () => {
    if (loading || !hasMore) return

    setLoading(true)
    try {
      const nextPage = page + 1
      const notifs = await getUserNotifications(`page=${nextPage}&pageSize=${count}`)
      const { notifications: notificationData, unreadCount } = notifs.data

      if (notificationData.length === 0) {
        setHasMore(false)
      } else {
        setPage(nextPage)
        handleSetNotificationList(
          notificationList.concat(notificationData),
          unreadCount
        )
      }
    } catch (error) {
      console.error('Failed to load more notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatMessage = (notification: Notification) => {
    const { contact, message, botmap } = notification
    const messageData = [
    //   { field: 'contactName', value: contact.name },
      { field: 'botName', value: botmap?.bot?.name || '' },
    ]
    let msg = formatUtils.formatMessage(messageData, message)
    return msg.replace('Default long code', 'your texting number')
  }

  const getNotificationCategory = (notification: Notification) => {
    const { header } = notification
    if (header.toLowerCase().includes('message') || header.toLowerCase().includes('sms')) {
      return 'Message'
    } else if (header.toLowerCase().includes('apply') || header.toLowerCase().includes('application')) {
      return 'Application'
    } else if (header.toLowerCase().includes('schedule') || header.toLowerCase().includes('interview')) {
      return 'Interview'
    }
    return 'Activity'
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Message': return '#4caf50'
      case 'Application': return '#2196f3'
      case 'Interview': return '#ff9800'
      default: return '#9e9e9e'
    }
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Message': return <MessageOutlined />
      case 'Application': return <FileTextOutlined />
      case 'Interview': return <CalendarOutlined />
      default: return <BellOutlined />
    }
  }

  if (!notificationList || notificationList.length === 0) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center' }}>
        <Empty 
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No notifications yet"
        />
      </div>
    )
  }

  return (
    <div className="notification-list-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
      <List
        itemLayout="horizontal"
        dataSource={notificationList}
        split={false}
        loadMore={
          hasMore && notificationList.length >= count ? (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <Button 
                onClick={loadMore} 
                loading={loading}
                type="link"
                size="small"
                style={{ color: '#666', fontSize: 12 }}
              >
                Load more
              </Button>
            </div>
          ) : null
        }
        renderItem={(notification: Notification) => {
          const isUnread = !notification.is_read
          const category = getNotificationCategory(notification)
          const categoryColor = getCategoryColor(category)
          const { contact } = notification

          const menuItems = [
            {
              key: 'markRead',
              label: 'Mark as read',
              icon: <CheckOutlined />,
              disabled: notification.is_read,
            },
          ]

          return (
            <div
              onClick={() => handleClickNotif(notification)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                padding: '10px 4px',
                cursor: 'pointer',
                backgroundColor: isUnread ? '#f2f8f4' : 'transparent',
                transition: 'background-color 0.15s ease',
                borderBottom: '1px solid #f5f5f5',
              }}
              className="notification-item-row"
            >
              {/* Category Icon */}
              <div
                style={{
                  width: 28,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  color: '#888',
                  fontSize: 14,
                  marginTop: 1,
                }}
              >
                {getCategoryIcon(category)}
              </div>

              {/* Three-line content: name+timestamp, brand tag (multi-brand only), preview */}
              <div style={{ flex: 1, minWidth: 0, marginLeft: 4 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 12,
                  lineHeight: '18px',
                }}>
                  <span style={{
                    color: '#111',
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                  }}>
                    {contact.name}
                  </span>
                  <span style={{
                    color: '#999',
                    fontSize: 11,
                    flexShrink: 0,
                    paddingLeft: 4,
                  }}>
                    {formatTimeAgo(notification.created_at)}
                  </span>
                </div>
                {isMultiBrand && notification.brand?.name && (
                  <div style={{ marginTop: 2, lineHeight: '16px' }}>
                    <Tag
                      color={notification.viaUserId ? 'blue' : 'default'}
                      style={{
                        margin: 0,
                        fontSize: 10,
                        lineHeight: '16px',
                        padding: '0 6px',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {notification.brand.name}
                    </Tag>
                  </div>
                )}
                <div style={{
                  color: '#555',
                  fontSize: 12,
                  lineHeight: '18px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  marginTop: 1,
                }}>
                  {formatMessage(notification)}
                </div>
              </div>

              {/* More menu */}
              <Dropdown
                menu={{
                  items: menuItems,
                  onClick: ({ key, domEvent }) => {
                    domEvent.stopPropagation()
                    if (key === 'markRead') {
                      handleMarkAsRead(domEvent as any, notification)
                    }
                  }
                }}
                trigger={['click']}
                placement="bottomRight"
              >
                <Button
                  type="text"
                  icon={<MoreOutlined />}
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    color: '#999',
                    flexShrink: 0,
                    marginLeft: 4,
                  }}
                />
              </Dropdown>
            </div>
          )
        }}
      />
      
      <style>{`
        .notification-item-row:hover {
          background-color: #fafafa !important;
        }
        .notification-list-container::-webkit-scrollbar {
          width: 6px;
          height: 6px;
          background-color: transparent;
        }
        .notification-list-container::-webkit-scrollbar-track {
          background-color: transparent;
          border-radius: 6px;
        }
        .notification-list-container::-webkit-scrollbar-thumb {
          border-radius: 6px;
          background-color: rgba(76, 175, 80, 0.2);
          transition: background-color 0.2s ease;
        }
        .notification-list-container::-webkit-scrollbar-thumb:hover {
          background-color: rgba(76, 175, 80, 0.7);
        }
        .notification-list-container::-webkit-scrollbar-thumb:active {
          background-color: #4CAF50;
        }
      `}</style>
    </div>
  )
}

const mapStateToProps = (state: any) => {
  return { ...state.modules }
}

const mapDispatchToProps = (dispatch: any) => {
  return {
    goTo(url: string) {
      return () => {
        return dispatch(push(url))
      }
    },
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(NotificationListAntd)
